import { View } from "react-native";
import { ModDashboardThreadDetails } from "./moddashboardthreads";
import { useDatastore, useModuleMap, useModuleObject } from "util/datastore";
import { useModerationAuthorById, useModerationAuthorsByIds } from 'open-modules/zdf/component/moderation';
import { useEffect, useState } from "react";
import { ModDashboardQueueListItem } from "./moddashboardqueue";
import { ModerationFiltersDefault } from "./moddashboardFilters";
import { useInstanceParams } from "util/params";
import { PadBox } from "component/basics";
import { ExpandButton } from "component/button";
import { scrollTo } from "platform-specific/navigation.web";
import { useParentDatastore } from 'open-modules/zdf/component/parentdatastore';
import { moduleMapQueue, ModTask, ModAuthor, moduleMapQueueCount } from "./moddashboardtypes";
import { FacePile } from "component/people";


export function ModTaskTreeView() {
    const params = useInstanceParams();
    const parentDatastore = useParentDatastore();
    const modTaskKey = params?.modTaskTreeKey;

    const selectedModTask = useModuleObject(moduleMapQueue, modTaskKey);
    const [rootTreeNodes, setRootTreeNodes] = useState<ModTaskTreeNode[]>([]);
    const [highlightedPathKeys, setHighlightedPathKeys] = useState([] as string[]);

    // Retrieve all mod tasks from this thread
    const modTasks = useModTasksFromCurrentInstance();

    useEffect(() => {
        if (selectedModTask) {
            setRootTreeNodes([new ModTaskTreeNode(selectedModTask)]);
        }
    }, [selectedModTask]);

    useEffect(() => {
        if (modTasks && modTasks.length > 0) {
            const treeResult = constructTree({ modTasks: modTasks });
            setRootTreeNodes(treeResult);

            const pathKeysResult = getModTaskKeysOnPath({ modTasks, targetModTaskKey: modTaskKey, pathKeys: [] });
            setHighlightedPathKeys(pathKeysResult);
        }
    }, [modTasks]);

    useEffect(() => {
        highlightSelectedModTask({ modTaskKey: modTaskKey, datastore: parentDatastore });
    }, []);

    return (
        <View>
            <ModDashboardThreadDetails includeTabs={false} />
            {rootTreeNodes && rootTreeNodes.length > 0 && (
                <ModTaskTree treeNodes={rootTreeNodes} highlightedPathKeys={highlightedPathKeys} />
            )}
        </View>
    );
}

/** Retrieves all mod tasks from the current thread/instance */
function useModTasksFromCurrentInstance() {
    const datastore = useDatastore();
    const [modTasks, setModTasks] = useState([] as ModTask[]);
    const queueCounts = useModuleMap(moduleMapQueueCount) || {};
    const moderationFilterIds = Object.values(ModerationFiltersDefault).map((moderationFilterObject) => {
        return moderationFilterObject.id;
    });

    async function fetchModTasksAsync() {
        const resultModTasks = await datastore.callServerAsync("moderationZdf", "getModTasksFromCurrentInstance", {
            moderationFilterIds: moderationFilterIds,
        });
        setModTasks(resultModTasks);
    }

    // TODO: queueCounts may not the best dependency to use. A change in queueCounts just means there was a change in one of the threads, not necessarily in the one you're looking at. A timestamp for the last change in each thread might be better but nothing like that exists at the moment.
    useEffect(() => {
        if (Object.keys(queueCounts).length > 0) {
            fetchModTasksAsync();
        }
    }, [queueCounts]);

    return modTasks;
}

import type { Datastore } from "util/datastore";

function highlightSelectedModTask({ modTaskKey, datastore }: { modTaskKey: string, datastore: Datastore }): void {
    datastore.setSessionData(['modTask-selected-idx'], modTaskKey);
    const elementID = "card-" + modTaskKey;

    setTimeout(() => {
        // TODO: This is platform-specific to web. Is there a universal alternative?
        scrollTo({ elementID, block: "start" });
    }, 500);
}

class ModTaskTreeNode {
    modTask: ModTask;
    parent: ModTaskTreeNode | undefined;
    children: ModTaskTreeNode[];

    constructor(modTask: ModTask) {
        this.modTask = modTask;
        this.children = [];
    }

    addChild(child: ModTaskTreeNode): void {
        child.parent = this;
        this.children.push(child);
    }
}

function ModTaskTree({ treeNodes, highlightedPathKeys }: { treeNodes: ModTaskTreeNode[], highlightedPathKeys: string[] }) {
    return (
        <PadBox bottom={80}>
            {treeNodes.map(
                (treeNode) =>
                    treeNode.modTask && (
                        <ModTaskTreeBranch
                            key={"modtasktreebranch-" + treeNode.modTask?.key}
                            treeNode={treeNode}
                            highlightedPathKeys={highlightedPathKeys}
                        />
                    )
            )}
        </PadBox>
    );
}

function ModTaskTreeBranch({ treeNode, highlightedPathKeys }: { treeNode: ModTaskTreeNode, highlightedPathKeys: string[] }) {
    const { modTask, children } = treeNode;
    const isOnPath = !!highlightedPathKeys?.find((pathKey) => pathKey === modTask.key);
    const [shouldExpandChildren, setShouldExpandChildren] = useState(isOnPath);

    const modAuthorMap = useModerationAuthorById(modTask?.key) || null;

    const childrenModAuthorMap = useModerationAuthorsByIds(children.map((childTreeNode) => childTreeNode.modTask?.key));
    const childrenModAuthors: ModAuthor[] = Object.values(childrenModAuthorMap);
    const childrenModAuthorIds = childrenModAuthors.map((childModAuthor) => childModAuthor.authorKey);

    return (
        <View>
            <PadBox left={treeNode.parent ? 40 : 0}>
                {modTask && modAuthorMap && (
                    <ModDashboardQueueListItem modTask={modTask} modAuthor={modAuthorMap[modTask.key]} />
                )}
                {children.length > 0 && (
                    <PadBox top={16}>
                        <ExpandButton
                            summaryElement={<FacePile type='tiny' userIdList={childrenModAuthorIds} />}
                            label="{count} {noun}"
                            expanded={shouldExpandChildren}
                            setExpanded={setShouldExpandChildren}
                            testID="toggle-replies"
                            formatParams={{ count: children.length, singular: "reply", plural: "replies" }}
                        />
                    </PadBox>
                )}
                {shouldExpandChildren &&
                    children?.map((childTreeNode, index) => (
                        <ModTaskTreeBranch key={childTreeNode.modTask.key + index} treeNode={childTreeNode} highlightedPathKeys={highlightedPathKeys} />
                    ))}
            </PadBox>
        </View>
    );
}

function constructTree({ modTasks }: { modTasks: ModTask[] }): ModTaskTreeNode[] {
    const flatModTasks = modTasks;

    const rootModTasks = flatModTasks.filter(
        (modTask) => modTask.object?.replyTo === null || modTask.object?.replyTo === undefined
    );

    const rootTreeNodes: ModTaskTreeNode[] = rootModTasks.map((rootModTask) => {
        return new ModTaskTreeNode(rootModTask);
    });

    rootTreeNodes.forEach((rootTreeNode) => {
        populateTreeChildren({ treeNode: rootTreeNode, flatModTasks });
    });

    return rootTreeNodes;
}

function populateTreeChildren({ treeNode, flatModTasks }: { treeNode: ModTaskTreeNode, flatModTasks: ModTask[] }): void {
    const treeNodeKey = treeNode.modTask.key;

    flatModTasks.forEach((modTask) => {
        if (modTask.object?.replyTo === treeNodeKey) {
            const childTreeNode = new ModTaskTreeNode(modTask);
            treeNode.addChild(childTreeNode);
            populateTreeChildren({ treeNode: childTreeNode, flatModTasks });
        }
    });
}

/** Returns the keys of all mod tasks in the reply chain that leads to the target mod task.
 * The top-level comment is included, the target mod task is excluded. */
function getModTaskKeysOnPath({ modTasks, targetModTaskKey, pathKeys = [] }: { modTasks: ModTask[], targetModTaskKey: string, pathKeys: string[] })  {
    const modTaskKeysOnPath: string[] = pathKeys;

    const modTaskFound = modTasks.find((modTask) => modTask.key === targetModTaskKey);
    if (modTaskFound?.object?.replyTo !== null && modTaskFound?.object?.replyTo !== undefined) {
        modTaskKeysOnPath.push(modTaskFound.object.replyTo);
        getModTaskKeysOnPath({ modTasks, targetModTaskKey: modTaskFound.object.replyTo, pathKeys: modTaskKeysOnPath });
    }

    return modTaskKeysOnPath;
}